import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Navbar, Nav, Button, Col, Row, Alert, Badge, Dropdown } from 'react-bootstrap';
import PlayerRegistrationForm from './components/PlayerRegistrationForm';
import PlayerSearch from './components/PlayerSearch';
import PlayerChat from './components/PlayerChat'; // Import PlayerChat
import MapComponent from './components/MapComponent'; // Import MapComponent
import footballLogo from './assets/football-logo.svg'; // Import the football logo image
import PlayerDashboard from './components/PlayerDashboard';
import { loQueSeaAFloat } from './utils/TypeUtils';

const backendUrl = '/api';
const HEARTBEAT_INTERVAL = 30 * 1000; // 30 seconds

interface AppProps {
  initialActiveView?: 'register' | 'search';
}

interface Player {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  isOnline: boolean;
  contact?: string;
}

function App({ initialActiveView: propInitialActiveView }: AppProps) {

  const { t, i18n } = useTranslation();

  const [foundPlayers, setFoundPlayers] = useState<Player[]>([]); // This will store players found by search
  const [selectedLocation, setSelectedLocation] = useState<{ latitude: number; longitude: number } | null>(null); // New state for map selected location

  const handleMapClickForRegistration = (latlng: { lat: number; lng: number }) => {
    setSelectedLocation({ latitude: latlng.lat, longitude: latlng.lng });
  };

  const [message, setMessage] = useState<{ type: 'success' | 'danger'; text: string } | null>(null);

  const initialActiveView = propInitialActiveView || localStorage.getItem('activeView') as 'register' | 'search' || 'search';
  const [activeView, setActiveView] = useState<'register' | 'search'>(initialActiveView); // 'register' or 'search'
  const [registeredPlayerId, setRegisteredPlayerId] = useState<string | null>(null);
  const [registeredPlayerLocation, setRegisteredPlayerLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [registeredPlayerName, setRegisteredPlayerName] = useState<string | null>(null); // New state for registered player's name
  const [lastSearchParams, setLastSearchParams] = useState<{ latitude: number; longitude: number; radius: number } | null>(null);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState<number>(0); // New state for unread messages count
  const [unreadMessagesCountSearcher, setUnreadMessagesCountSearcher] = useState<number>(0); // New state for unread messages count for searchers
  const [showNotificationsDropdown, setShowNotificationsDropdown] = useState<boolean>(false); // State for dropdown visibility
  const [openChatWindows, setOpenChatWindows] = useState<Array<{ id: string; name: string; gameAddress: string; gameDate: string; gameTime: string }>>([]); // State to manage open chat windows (by searcherId and name)

  const onNewMessage = (newSearcherId: string) => {
    setUnreadMessagesCount(prev => prev + 1);
    setOpenChatWindows(prev => {
      if (!prev.some(c => c.id === newSearcherId)) {
        return [...prev, { id: newSearcherId, name: `Searcher ${newSearcherId.substring(0, 4)}`, gameAddress: '', gameDate: '', gameTime: '' }]; // Add new chat with placeholder name
      }
      return prev;
    });
  };

  const [searcherNameApp, setSearcherNameApp] = useState<string | null>(null); // New state for searcher's name in App
  const [gameAddressApp, setGameAddressApp] = useState<string | null>(null); // New state for searcher's game address in App
  const [gameDateApp, setGameDateApp] = useState<string | null>(null); // New state for searcher's game date in App
  const [gameTimeApp, setGameTimeApp] = useState<string | null>(null); // New state for searcher's game time in App

  console.log('App Render: localStorage searcherName:', localStorage.getItem('searcherName'));
  console.log('App Render: localStorage gameAddress:', localStorage.getItem('gameAddress'));

  const handleSearcherNameChange = (name: string) => {
    setSearcherNameApp(name);
  };

  const handleSearcherGameAddressChange = (address: string) => {
    setGameAddressApp(address);
  };

  const handleSearcherGameDateChange = (date: string) => {
    setGameDateApp(date);
  };

  const handleSearcherGameTimeChange = (time: string) => {
    setGameTimeApp(time);
  };

  const handleBellClick = () => {
    console.log('App: Bell icon clicked. Setting activeView to register.');
    setShowNotificationsDropdown(prev => !prev); // Toggle dropdown visibility
    if (!showNotificationsDropdown) { // If opening dropdown, reset count
      setUnreadMessagesCount(0);
      setUnreadMessagesCountSearcher(0);
    }
  };

  const handleViewAllMessages = () => {
    setActiveView('register'); // Switch to the register view to show PlayerChat
    setShowNotificationsDropdown(false); // Hide dropdown
  };

  const handleCloseChat = (searcherId: string) => {
    setOpenChatWindows(prev => prev.filter(chat => chat.id !== searcherId));
  };

  const handleOpenChatWithSearcher = (searcherId: string, searcherName: string, gameAddress: string, gameDate: string, gameTime: string) => {
    setOpenChatWindows(prev => {
      if (!prev.some(chat => chat.id === searcherId)) {
        return [...prev, { id: searcherId, name: searcherName, gameAddress, gameDate, gameTime }];
      }
      return prev;
    });
  };

  const changeLanguage = (lng: string) => {

    i18n.changeLanguage(lng);

  };



  const handleClearData = async () => {
    const searcherUniqueId = localStorage.getItem('searcherUniqueId');

    // Clear local storage
    localStorage.removeItem('searcherUniqueId');
    localStorage.removeItem('searcherName');
    localStorage.removeItem('gameAddress');
    localStorage.removeItem('gameDate');
    localStorage.removeItem('gameTime');
    localStorage.removeItem('location');
    localStorage.removeItem('activeChats');

    // Reset states in App.tsx
    setSearcherNameApp(null);
    setGameAddressApp(null);
    setGameDateApp(null);
    setGameTimeApp(null);
    setFoundPlayers([]);
    setLastSearchParams(null);
    setMessage(null);
    // Force a reload of the PlayerSearch component to re-initialize its state
    // This can be done by changing the activeView or a key prop, but for simplicity, a full page reload might be acceptable here
    // For a more controlled approach, PlayerSearch would need a prop to trigger a reset.
    window.location.reload();
  };

  const handleClearPlayerData = async () => {
    const playerId = registeredPlayerId;

    // Clear local storage
    localStorage.removeItem('registeredPlayerId');
    localStorage.removeItem('registeredPlayerLocation');
    localStorage.removeItem('registeredPlayerName');

    // Reset states in App.tsx
    setRegisteredPlayerId(null);
    setRegisteredPlayerLocation(null);
    setRegisteredPlayerName(null);
    setMessage(null);

    // Call backend to logically delete player
    if (playerId) {
      try {
        const response = await fetch(`${backendUrl}/api/players/${playerId}`, {
          method: 'DELETE',
        });
        if (response.ok) {
          setMessage({ type: 'success', text: t('playerDataCleared') }); // New translation key needed
          console.log('Player data logically deleted from backend.');
        } else {
          const errorData = await response.json();
          setMessage({ type: 'danger', text: t('playerDataClearError', { message: errorData.message || 'Unknown error' }) }); // New translation key needed
          console.error('Error logically deleting player from backend:', errorData.message);
        }
      } catch (error: any) {
        setMessage({ type: 'danger', text: t('playerDataClearError', { message: error.message }) });
        console.error('Network error logically deleting player from backend:', error);
      }
    }
    // Force a reload of the page to re-initialize its state
    window.location.reload();
  };

  const handleRegister = async (playerData: { name: string; contact: string; latitude: number; longitude: number }) => {

    try {

      const response = await fetch(`${backendUrl}/api/players`, {

        method: 'POST',

        headers: {

          'Content-Type': 'application/json',

        },

        body: JSON.stringify(playerData),

      });



      const data = await response.json();

      if (response.ok) {

                setMessage({ type: 'success', text: t('registrationSuccess') });

                        setRegisteredPlayerId(data.player.id); // Store the registered player's ID

                        setRegisteredPlayerLocation({ latitude: playerData.latitude, longitude: playerData.longitude }); // Store the registered player's location

                        setRegisteredPlayerName(playerData.name); // Store the registered player's name

                        console.log('Registered Player ID:', data.player.id);

        console.log('Player registered successfully:', data.player);

      } else {

        setMessage({ type: 'danger', text: t('registrationError', { message: data.message || 'Unknown error' }) });

        console.error('Error registering player:', data.message);

      }

    } catch (error: any) {

      setMessage({ type: 'danger', text: t('registrationError', { message: error.message }) });

      console.error('Network error during registration:', error);

    }

  };



  const handleSearch = async (searchParams: { latitude: number; longitude: number; radius: number }) => {

    try {

      const { latitude, longitude, radius } = searchParams;

      const response = await fetch(`${backendUrl}/api/players?latitude=${latitude}&longitude=${longitude}&radius=${radius}`);

      const data = await response.json();



      if (response.ok) {

        const typedPlayers: Player[] = data.players.map((p: any) => ({
          ...p,
          latitude: loQueSeaAFloat(p.latitude),
          longitude: loQueSeaAFloat(p.longitude),
        }));
        setFoundPlayers(typedPlayers);

        setLastSearchParams(searchParams); // Store last successful search params

        setMessage({ type: 'success', text: t('searchSuccess', { count: data.players.length }) });

        console.log('Players found:', data.players);

      } else {

        setFoundPlayers([]); // Clear players on error

        setMessage({ type: 'danger', text: t('searchError', { message: data.message || 'Unknown error' }) });

        console.error('Error searching for players:', data.message);

      }

    } catch (error: any) {

      setFoundPlayers([]); // Clear players on error

      setMessage({ type: 'danger', text: t('searchError', { message: error.message }) });

      console.error('Network error during search:', error);

    }

  };


  // Load from localStorage on mount
  useEffect(() => {
    const storedRegisteredPlayerId = localStorage.getItem('registeredPlayerId');
    console.log('App useEffect: Loading registeredPlayerId from localStorage:', storedRegisteredPlayerId);
    if (storedRegisteredPlayerId) {
      const verifyPlayerId = async (playerId: string) => {
        try {
          const response = await fetch(`${backendUrl}/api/players/${playerId}/exists`);
          if (response.ok) {
            const data = await response.json();
            if (!data.exists) {
              console.log(`App useEffect: Player ID ${playerId} does not exist in backend. Clearing local storage.`);
              setRegisteredPlayerId(null);
              setRegisteredPlayerLocation(null);
              setRegisteredPlayerName(null);
              localStorage.removeItem('registeredPlayerId');
              localStorage.removeItem('registeredPlayerLocation');
              localStorage.removeItem('registeredPlayerName');
            } else {
              setRegisteredPlayerId(playerId);
              console.log('App useEffect: setRegisteredPlayerId to', playerId);
            }
          } else {
            console.error('App useEffect: Error verifying player ID with backend:', await response.json());
            setRegisteredPlayerId(null);
            setRegisteredPlayerLocation(null);
            setRegisteredPlayerName(null);
            localStorage.removeItem('registeredPlayerId');
            localStorage.removeItem('registeredPlayerLocation');
            localStorage.removeItem('registeredPlayerName');
          }
        } catch (error) {
          console.error('App useEffect: Network error verifying player ID:', error);
          setRegisteredPlayerId(null);
          setRegisteredPlayerLocation(null);
          setRegisteredPlayerName(null);
          localStorage.removeItem('registeredPlayerId');
          localStorage.removeItem('registeredPlayerLocation');
          localStorage.removeItem('registeredPlayerName');
        }
      };
      verifyPlayerId(storedRegisteredPlayerId);
    }

    const storedRegisteredPlayerLocation = localStorage.getItem('registeredPlayerLocation');
    console.log('App useEffect: Loading registeredPlayerLocation from localStorage:', storedRegisteredPlayerLocation);
    if (storedRegisteredPlayerLocation) {
      setRegisteredPlayerLocation(JSON.parse(storedRegisteredPlayerLocation));
      console.log('App useEffect: setRegisteredPlayerLocation to', storedRegisteredPlayerLocation);
    }

    const storedRegisteredPlayerName = localStorage.getItem('registeredPlayerName');
    console.log('App useEffect: Loading registeredPlayerName from localStorage:', storedRegisteredPlayerName);
    if (storedRegisteredPlayerName) {
      setRegisteredPlayerName(storedRegisteredPlayerName);
      console.log('App useEffect: setRegisteredPlayerName to', storedRegisteredPlayerName);
    }

    const storedSearcherName = localStorage.getItem('searcherName');
    console.log('App useEffect: Loading searcherName from localStorage:', storedSearcherName);
    if (storedSearcherName) {
      setSearcherNameApp(storedSearcherName);
      console.log('App useEffect: setSearcherNameApp to', storedSearcherName);
    }

    const storedGameAddress = localStorage.getItem('gameAddress');
    console.log('App useEffect: Loading gameAddress from localStorage:', storedGameAddress);
    if (storedGameAddress) {
      setGameAddressApp(storedGameAddress);
      console.log('App useEffect: setGameAddressApp to', storedGameAddress);
    }

    const storedGameDate = localStorage.getItem('gameDate');
    console.log('App useEffect: Loading gameDate from localStorage:', storedGameDate);
    if (storedGameDate) {
      setGameDateApp(storedGameDate);
      console.log('App useEffect: setGameDateApp to', storedGameDate);
    }

    const storedGameTime = localStorage.getItem('gameTime');
    console.log('App useEffect: Loading gameTime from localStorage:', storedGameTime);
    if (storedGameTime) {
      setGameTimeApp(storedGameTime);
      console.log('App useEffect: setGameTimeApp to', storedGameTime);
    }
  }, []); // Empty dependency array means this runs once on mount


  // Save activeView to localStorage
  useEffect(() => {
    console.log('App: Saving activeView to localStorage:', activeView);
    localStorage.setItem('activeView', activeView);
  }, [activeView]);

  useEffect(() => {
    if (registeredPlayerId) {
      localStorage.setItem('registeredPlayerId', registeredPlayerId);
    } else {
      localStorage.removeItem('registeredPlayerId');
    }
  }, [registeredPlayerId]);

  useEffect(() => {
    if (registeredPlayerLocation) {
      localStorage.setItem('registeredPlayerLocation', JSON.stringify(registeredPlayerLocation));
    } else {
      localStorage.removeItem('registeredPlayerLocation');
    }
  }, [registeredPlayerLocation]);

  useEffect(() => {
    if (registeredPlayerName) {
      localStorage.setItem('registeredPlayerName', registeredPlayerName);
    } else {
      localStorage.removeItem('registeredPlayerName');
    }
  }, [registeredPlayerName]);

  useEffect(() => {
    if (searcherNameApp) {
      console.log('App: searcherNameApp changed to:', searcherNameApp);
    }
  }, [searcherNameApp]);

  useEffect(() => {
    if (gameAddressApp) {
      console.log('App: gameAddressApp changed to:', gameAddressApp);
    }
  }, [gameAddressApp]);

  useEffect(() => {
    console.log('App: Conditional check for dynamic text:', searcherNameApp, gameAddressApp, searcherNameApp && gameAddressApp);
  }, [searcherNameApp, gameAddressApp]);

  useEffect(() => {

    console.log('App: Active View Changed:', activeView);

  }, [activeView]);



  useEffect(() => {

    let heartbeatInterval: NodeJS.Timeout | undefined;



    if (registeredPlayerId) {

      heartbeatInterval = setInterval(async () => {

        try {

          await fetch(`${backendUrl}/api/players/${registeredPlayerId}/heartbeat`, {

            method: 'POST',

          });

          console.log(`Heartbeat sent for player ${registeredPlayerId}`);

        } catch (error) {

          console.error(`Error sending heartbeat for player ${registeredPlayerId}:`, error);

        }

      }, HEARTBEAT_INTERVAL);

    }



    return () => {

      if (heartbeatInterval) {

        clearInterval(heartbeatInterval);

      }

    };

  }, [registeredPlayerId]);



  useEffect(() => {
    let isMounted = true;
    let longPollTimeout: NodeJS.Timeout | undefined;

    const pollForOnlineStatus = async () => {
      if (activeView !== 'search' || !lastSearchParams) return;

      try {
        const response = await fetch(`${backendUrl}/api/players/online-status`);
        const data = await response.json();

        if (isMounted && response.ok) {
          if (data.playersOnlineStatus && data.playersOnlineStatus.length > 0) {
            setFoundPlayers(prevPlayers => {
              const playerMap = new Map(prevPlayers.map(p => [p.id, p]));
              data.playersOnlineStatus.forEach((player: any) => {
                playerMap.set(player.id, player);
              });
              return Array.from(playerMap.values());
            });
          }
        }
      } catch (error) {
        console.error('Long polling error:', error);
      } finally {
        if (isMounted) {
          longPollTimeout = setTimeout(pollForOnlineStatus, 1000);
        }
      }
    };

    if (activeView === 'search' && lastSearchParams) {
      pollForOnlineStatus();
    }

    return () => {
      isMounted = false;
      if (longPollTimeout) {
        clearTimeout(longPollTimeout);
      }
    };
  }, [activeView, lastSearchParams]); // Depend on activeView and lastSearchParams

  useEffect(() => {
    let isMounted = true;
    let longPollTimeout: NodeJS.Timeout | undefined;

    const pollForNewChats = async () => {
      if (!registeredPlayerId) return;

      try {
        const response = await fetch(`${backendUrl}/api/chats/${registeredPlayerId}`);
        const data = await response.json();

        if (isMounted && response.ok && data.chatSessions) {
          console.log('App: pollForNewChats received chatSessions:', data.chatSessions);
          const chatSessionsWithDetails = await Promise.all(data.chatSessions.map(async (session: { id: string; name: string }) => {
            try {
              const detailsResponse = await fetch(`${backendUrl}/api/searchers/${session.id}/details`);
              if (detailsResponse.ok) {
                const details = await detailsResponse.json();
                return { ...session, gameAddress: details.gameAddress, gameDate: details.gameDate, gameTime: details.gameTime };
              } else {
                console.error(`Error fetching details for searcher ${session.id}:`, await detailsResponse.json());
                return { ...session, gameAddress: 'Unknown Address', gameDate: 'Unknown Date', gameTime: 'Unknown Time' };
              }
            } catch (error) {
              console.error(`Network error fetching details for searcher ${session.id}:`, error);
              return { ...session, gameAddress: 'Unknown Address', gameDate: 'Unknown Date', gameTime: 'Unknown Time' };
            }
          }));

          setOpenChatWindows(prev => {
            const newChatEntries = chatSessionsWithDetails
              .filter((chatSession: { id: string; name: string; gameAddress: string; gameDate: string; gameTime: string }) => !prev.some(chat => chat.id === chatSession.id))
              .map((chatSession: { id: string; name: string; gameAddress: string; gameDate: string; gameTime: string }) => ({ id: chatSession.id, name: chatSession.name, gameAddress: chatSession.gameAddress, gameDate: chatSession.gameDate, gameTime: chatSession.gameTime }));

            if (newChatEntries.length > 0) {
              return [...prev, ...newChatEntries];
            }
            return prev;
          });
        }
      } catch (error) {
        console.error('Long polling for new chats error:', error);
      } finally {
        if (isMounted) {
          longPollTimeout = setTimeout(pollForNewChats, 5000); // Poll every 5 seconds
        }
      }
    };

    if (registeredPlayerId) {
      pollForNewChats();
    }

    return () => {
      isMounted = false;
      if (longPollTimeout) {
        clearTimeout(longPollTimeout);
      }
    };
  }, [registeredPlayerId]);

      return (
    <div className="App">
      <Navbar bg="dark" variant="dark" expand="lg">
        <Container>
          <Navbar.Brand href="/">
            <img
              src={footballLogo}
              height="30"
              className="d-inline-block align-top me-2"
              alt="Football Logo"
            />
            {t('welcome')}
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="me-auto">
              <Nav.Link
                onClick={() => {
                  console.log('App: Navigating to register view');
                  setActiveView('register');
                }}
                active={activeView === 'register'}
              >
                {t('wantToPlay')}
              </Nav.Link>
              <Nav.Link
                onClick={() => {
                  console.log('App: Navigating to search view');
                  setActiveView('search');
                }}
                active={activeView === 'search'}
              >
                {t('needPlayers')}
              </Nav.Link>
            </Nav>
            <Nav>
              {registeredPlayerName && <Navbar.Text className="text-light me-3">{t('hello', { name: registeredPlayerName })}</Navbar.Text>}
              {!registeredPlayerName && searcherNameApp && <Navbar.Text className="text-light me-3">{t('hello', { name: searcherNameApp })}</Navbar.Text>}
              <Dropdown className="me-2">
                <Dropdown.Toggle variant="outline-light" id="language-dropdown">
                  {i18n.language === 'es' ? '🇪🇸 ES' : i18n.language === 'es_AR' ? '🇦🇷 ES' : '🇬🇧 EN'}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => changeLanguage('es_AR')}>🇦🇷 Español</Dropdown.Item>
                  <Dropdown.Item onClick={() => changeLanguage('es')}>🇪🇸 Español</Dropdown.Item>
                  <Dropdown.Item onClick={() => changeLanguage('en')}>🇬🇧 English</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              <Dropdown show={showNotificationsDropdown} onToggle={handleBellClick} align="end">
                <Dropdown.Toggle as={Nav.Link} id="notifications-dropdown-toggle" className="position-relative">
                  <i className="bi bi-bell"></i> {/* Bell icon */}
                  {unreadMessagesCount + unreadMessagesCountSearcher > 0 && (
                    <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                      {unreadMessagesCount + unreadMessagesCountSearcher}
                      <span className="visually-hidden">unread messages</span>
                    </span>
                  )}
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  <Dropdown.Header>{t('notifications')}</Dropdown.Header>
                  {unreadMessagesCount > 0 && (
                    <Dropdown.ItemText>{t('youHaveNewMessagesPlayer', { count: unreadMessagesCount })}</Dropdown.ItemText>
                  )}
                  {unreadMessagesCountSearcher > 0 && (
                    <Dropdown.ItemText>{t('youHaveNewMessagesSearcher', { count: unreadMessagesCountSearcher })}</Dropdown.ItemText>
                  )}
                  {unreadMessagesCount === 0 && unreadMessagesCountSearcher === 0 && (
                    <Dropdown.ItemText>{t('noNewNotifications')}</Dropdown.ItemText>
                  )}
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={handleViewAllMessages}>{t('viewAllMessages')}</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <Container className="mt-4">
        <h1 className="text-center my-4" hidden>{t('welcome')}</h1>
        {message && (
          <Alert variant={message.type} dismissible onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        )}
        <Row>
          <Col md={12}>
            {activeView === 'register' && (
              <>
                <Row className="align-items-center mb-3">
<Col>
<h2>{t('wantToPlay')}</h2>
</Col>
{registeredPlayerId && (
<Col xs="auto">
<Button variant="outline-danger" size="sm" onClick={handleClearPlayerData}>
{t('clearMyData')}
</Button>
</Col>
)}
</Row>
{!registeredPlayerId && (
  <PlayerRegistrationForm onRegister={handleRegister} selectedLocation={selectedLocation} />
)}
{console.log('App: registeredPlayerId is:', registeredPlayerId)}
<PlayerDashboard
  registeredPlayerId={registeredPlayerId}
  registeredPlayerLocation={registeredPlayerLocation}
  registeredPlayerName={registeredPlayerName}
  openChatWindows={openChatWindows}
  handleCloseChat={handleCloseChat}
  onNewMessage={onNewMessage}
  onOpenChatWithSearcher={handleOpenChatWithSearcher} // Pass the new callback
  onMapClickForRegistration={handleMapClickForRegistration} // Pass the map click handler
/>
                              </>
                            )}
                            {activeView === 'search' && (
                              <>
                                <Row className="align-items-center mb-3">
                                  <Col>
                                    <h2>
                                      {searcherNameApp && gameAddressApp
                                        ? t('needPlayersForGameAddress', { gameAddress: gameAddressApp })
                                        : t('needPlayers')}
                                    </h2>
                                  </Col>
                                  <Col xs="auto">
                                    <Button variant="outline-danger" size="sm" onClick={handleClearData}>
                                      {t('clearMyData')} {/* New translation key needed */}
                                    </Button>
                                  </Col>
                                </Row>
                                <PlayerSearch
                                  onSearch={handleSearch}
                                  players={foundPlayers}
                                  setMessage={setMessage}
                                  onSearcherNameChange={handleSearcherNameChange}
                                  searcherNameApp={searcherNameApp}
                                  onSearcherGameAddressChange={handleSearcherGameAddressChange} // Pass the new callback
                                  onSearcherGameDateChange={handleSearcherGameDateChange} // Pass the new callback
                                  onSearcherGameTimeChange={handleSearcherGameTimeChange} // Pass the new callback
                                />
                              </>
                            )}
                          </Col>
                        </Row>
                      </Container>
                    </div>
                  );
                }

export default App;